import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from '../types/shelter';
import { mockUsers } from '../data/mockData';
import { supabase } from '@/integrations/supabase/client';
import { User as SupabaseUser, Session } from '@supabase/supabase-js';
import { ROLES, AppRole, EffectiveRole, getEffectiveRole } from '@/constants/roles';

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string; needsPasswordReset?: boolean; mfaRequired?: boolean }>;
  signUp: (email: string, password: string, fullName: string, username: string, organizationName?: string) => Promise<{ success: boolean; error?: string; autoConfirmed?: boolean }>;
  createStaffUser: (email: string, password: string, fullName: string, username: string, role: AppRole) => Promise<{ success: boolean; error?: string; userId?: string }>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  /** @deprecated Always false — demo mode has been removed. Kept for backward compatibility. */
  isDemoMode: false;
  userRole: AppRole | null;
  effectiveRole: EffectiveRole | null;
  isTestUser: boolean;
  userSites: string[];
  organizationId: string | null;
  needsPasswordReset: boolean;
  setNeedsPasswordReset: (needs: boolean) => void;
  mfaPending: boolean;
  completeMFA: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Test user emails for secure test environment
const TEST_USER_EMAILS = [
  'superadmin@demo.local',
  'manager1@demo.local',
  'staff1@demo.local'
];

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<AppRole | null>(null);
  const [effectiveRole, setEffectiveRole] = useState<EffectiveRole | null>(null);
  const [isTestUser, setIsTestUser] = useState(false);
  const [userSites, setUserSites] = useState<string[]>([]);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [needsPasswordReset, setNeedsPasswordReset] = useState(false);
  const [mfaPending, setMfaPending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let bootstrapped = false;
    const finishBootstrap = () => {
      if (!bootstrapped) {
        bootstrapped = true;
        setIsLoading(false);
      }
    };

    // Set up Supabase auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        if (session?.user) {
          const needsReset = session.user.user_metadata?.needs_password_reset === true;
          setNeedsPasswordReset(needsReset);

          setTimeout(() => {
            loadUserProfile(session.user).finally(finishBootstrap);
          }, 0);
        } else {
          setUser(null);
          finishBootstrap();
        }
      }
    );

    // Check for existing Supabase session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        const needsReset = session.user.user_metadata?.needs_password_reset === true;
        setNeedsPasswordReset(needsReset);
        loadUserProfile(session.user).finally(finishBootstrap);
      } else {
        finishBootstrap();
      }
    }).catch(finishBootstrap);

    return () => subscription.unsubscribe();
  }, []);

  const loadUserProfile = async (supabaseUser: SupabaseUser) => {
    try {
      // Fetch profile; if missing (common when users are created via admin flows), create it on first login
      const {
        data: existingProfile,
        error: profileSelectError,
      } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', supabaseUser.id)
        .maybeSingle();

      if (profileSelectError) throw profileSelectError;

      let profile = existingProfile;

      if (!profile) {
        const defaultFullName =
          (supabaseUser.user_metadata as any)?.full_name || supabaseUser.email || 'User';

        // Use upsert to handle race conditions where profile may already exist
        const { data: createdProfile, error: profileUpsertError } = await supabase
          .from('profiles')
          .upsert(
            {
              id: supabaseUser.id,
              full_name: defaultFullName,
            },
            { onConflict: 'id', ignoreDuplicates: true }
          )
          .select('*')
          .single();

        if (profileUpsertError) {
          // If upsert fails, try fetching again (profile might exist with different RLS context)
          const { data: refetchedProfile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', supabaseUser.id)
            .single();
          
          profile = refetchedProfile;
        } else {
          profile = createdProfile;
        }
      }

      let { data: userRoleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', supabaseUser.id)
        .maybeSingle();

      if (roleError) throw roleError;

      // If no role exists, create a default 'staff' role
      if (!userRoleData) {
        const { data: newRole, error: roleInsertError } = await supabase
          .from('user_roles')
          .insert([
            {
              user_id: supabaseUser.id,
              role: 'staff',
            },
          ])
          .select()
          .single();

        if (roleInsertError) throw roleInsertError;
        userRoleData = newRole;
      }

      // Load user's assigned sites
      const { data: assignedSites, error: sitesError } = await supabase
        .from('user_sites')
        .select('site_id')
        .eq('user_id', supabaseUser.id);

      if (sitesError) throw sitesError;

      // Load user's organization
      const { data: orgMembership } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', supabaseUser.id)
        .maybeSingle();

      // Detect test users by email (removed insecure localStorage override)
      const isTest = TEST_USER_EMAILS.includes(supabaseUser.email || '');

      if (profile && userRoleData) {
        const actualRole = userRoleData.role as AppRole;
        const userData: User = {
          id: profile.id,
          username: profile.full_name || supabaseUser.email || '',
          name: profile.full_name || supabaseUser.email || '',
          role: actualRole as
            | 'staff'
            | 'supervisor'
            | 'manager'
            | 'super_admin'
            | 'program_director'
            | 'case_manager'
            | 'residential_aide',
        };
        setUser(userData);
        setUserRole(actualRole);
        setEffectiveRole(getEffectiveRole(actualRole));
        setIsTestUser(isTest);
        setUserSites(assignedSites?.map((s) => s.site_id) || []);
        setOrganizationId(orgMembership?.organization_id || null);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const login = async (username: string, password: string): Promise<{ success: boolean; error?: string; needsPasswordReset?: boolean; mfaRequired?: boolean }> => {
    // Try Supabase authentication with email
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: username.trim(),
        password: password,
      });

      if (error) {
        // Provide helpful error message if username was used instead of email
        if (!username.includes('@')) {
          return { success: false, error: 'Please use your email address to log in (not username)' };
        }
        return { success: false, error: error.message };
      }

      if (data.user) {
        // Check MFA status
        const { data: aalData, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        
        if (!aalError && aalData.currentLevel === 'aal1' && aalData.nextLevel === 'aal2') {
          // User has 2FA enrolled but hasn't verified yet this session
          await loadUserProfile(data.user);
          setMfaPending(true);
          return { success: true, mfaRequired: true };
        }

        await loadUserProfile(data.user);
        
        // Log the login activity
        await supabase.from('user_activity_logs').insert({
          user_id: data.user.id,
          action_type: 'login',
          user_agent: navigator.userAgent
        });
        
        // Update last login timestamp
        await supabase.from('profiles').update({
          last_login: new Date().toISOString()
        }).eq('id', data.user.id);
        
        // Check if user needs password reset
        const needsReset = data.user.user_metadata?.needs_password_reset === true;
        return { success: true, needsPasswordReset: needsReset };
      }

      return { success: false, error: 'Login failed' };
    } catch (error: any) {
      return { success: false, error: error?.message || 'An error occurred during login' };
    }
  };

  const signUp = async (email: string, password: string, fullName: string, username: string, organizationName?: string): Promise<{ success: boolean; error?: string; autoConfirmed?: boolean }> => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            full_name: fullName,
            username: username,
            organization_name: organizationName || 'My Organization'
          }
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      // Check if user was auto-confirmed (email confirmation disabled in Supabase)
      if (data.session) {
        // User is automatically signed in
        await loadUserProfile(data.user!);
        return { success: true, autoConfirmed: true };
      }

      return { success: true, autoConfirmed: false };
    } catch (error) {
      return { success: false, error: 'An error occurred during sign up' };
    }
  };

  const createStaffUser = async (email: string, password: string, fullName: string, username: string, role: 'staff' | 'supervisor' | 'manager'): Promise<{ success: boolean; error?: string; userId?: string }> => {
    try {
      // Create the user
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            full_name: fullName,
            username: username,
            needs_password_reset: true
          }
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (data.user) {
        // Assign role to the new user
        const { error: roleError } = await supabase
          .from('user_roles')
          .insert([{
            user_id: data.user.id,
            role: role
          }]);

        if (roleError) {
          return { success: false, error: `User created but role assignment failed: ${roleError.message}` };
        }

        return { success: true, userId: data.user.id };
      }

      return { success: false, error: 'User creation failed' };
    } catch (error: any) {
      return { success: false, error: error.message || 'An error occurred during user creation' };
    }
  };

  const completeMFA = () => {
    setMfaPending(false);
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setMfaPending(false);
  };

  const value = {
    user,
    login,
    signUp,
    createStaffUser,
    logout,
    isAuthenticated: !!user,
    isDemoMode: false as const,
    userRole,
    effectiveRole,
    isTestUser,
    userSites,
    organizationId,
    needsPasswordReset,
    setNeedsPasswordReset,
    mfaPending,
    completeMFA,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Re-export for convenience
export { AuthContext };